/**
 * App.tsx - Root component and route configuration for the GBRANDS application.
 *
 * This file is responsible for:
 *	 1. Setting up the React Router — the data router (createBrowserRouter +
 *		RouterProvider), which is what lets a page hold an in-app navigation
 *		for the unsaved-changes prompt on the work order and inspection forms
 *	 2. Nesting the three global context providers in the correct order:
 *		  AuthProvider -> AppProvider -> OfflineProvider
 *	 3. Defining all top-level routes and wrapping authenticated routes
 *		in the <ProtectedRoute> guard component
 *	 4. Rendering the SyncStatusBar at the top level for offline/sync feedback
 *	 5. Hooking up useBeforeUnload to warn users about unsaved offline data
 *
 * Provider nesting order matters:
 *	 - AuthProvider must be outermost (inside the router) because AppProvider
 *	   and OfflineProvider may depend on auth state.
 *	 - AppProvider comes next for company/location selection state.
 *	 - OfflineProvider is innermost so it can access both auth and app state if needed.
 *	 - SyncStatusBar and BeforeUnload must be inside OfflineProvider to access context.
 */
import React from 'react';
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate, Outlet } from 'react-router-dom';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClient, persistOptions } from './lib/queryClient';
import { AuthProvider } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import { OfflineProvider } from './context/OfflineContext';
import { useAuth } from './hooks/useAuth';
import { SyncStatusBar, AppLayout } from './components/organisms';
import { QuickAddUsersProvider } from './components/organisms/QuickAddUsersModal';
import { useBeforeUnload } from './hooks/useBeforeUnload';
import { useScrollToTop } from './hooks/useScrollToTop';

// --- Page imports ---
import LoginPage from './pages/LoginPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage/OAuthCallbackPage';
import DashboardPage from './pages/DashboardPage';
import WorkOrdersPage from './pages/WorkOrdersPage';
import WorkOrderDetailPage from './pages/WorkOrderDetailPage';
import WorkOrderCreatePage from './pages/WorkOrderCreatePage';
import ProjectWorkOrdersPage from './pages/ProjectWorkOrdersPage';
import ProjectWorkOrderCreatePage from './pages/ProjectWorkOrderCreatePage';
import ProjectWorkOrderDetailPage from './pages/ProjectWorkOrderDetailPage';
import RecurringWorkOrdersPage from './pages/RecurringWorkOrdersPage';
import RecurringInspectionsPage from './pages/RecurringInspectionsPage';
import RecurringSeriesDetailPage from './pages/RecurringSeriesDetailPage';
import InspectionsPage from './pages/InspectionsPage';
import InspectionDetailPage from './pages/InspectionDetailPage';
import InspectionCreatePage from './pages/InspectionCreatePage';
import BuildingsPage from './pages/BuildingsPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import ApiKeysPage from './pages/ApiKeysPage';
import CMSPage from './pages/CMSPage';
import CalendarPage from './pages/CalendarPage';
import MapPage from './pages/MapPage';
import NotFoundPage from './pages/NotFoundPage';
import HowToPage from './pages/HowToPage';
import ForcePasswordChangePage from './pages/ForcePasswordChangePage';

/**
 * ProtectedRoute - Route guard component that ensures only authenticated
 * users can access wrapped child routes.
 *
 * Behavior:
 *	 - While auth state is loading (initial session restore), shows a loading indicator.
 *	 - If the user is not authenticated, redirects to /login with `replace` to
 *	   avoid polluting the browser history stack.
 *	 - If authenticated, renders the child route content.
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { isAuthenticated, isLoading, passwordChangeDue } = useAuth();

	// Show a loading spinner/message while the auth session is being restored
	if (isLoading) {
		return (
			<div className="min-h-screen min-h-[100dvh] flex items-center justify-center bg-[#eae5e5]">
				<p className="text-brand-text font-sans">Loading...</p>
			</div>
		);
	}

	// Redirect unauthenticated users to the login page
	if (!isAuthenticated) {
		return <Navigate to="/login" replace />;
	}

	// Force password change — redirect to the dedicated page if the user's
	// account requires a password update (first login or admin password reset).
	// This prevents access to any app content until the password is changed.
	// A Super Admin signed in as the user skips it (see passwordChangeDue).
	if (passwordChangeDue) {
		return <Navigate to="/change-password" replace />;
	}

	return <>{children}</>;
};

/**
 * HowToRoute - the hidden /how-to guide, open to everyone.
 *
 * A first-time user is often sent the link before they have signed in, so it
 * must not bounce them to /login. Signed in (and past any forced password
 * change) it renders inside the normal app layout; otherwise it renders on
 * its own with a Sign in link of its own.
 */
const HowToRoute: React.FC = () => {
	const { isAuthenticated, isLoading, passwordChangeDue } = useAuth();

	if (isLoading) {
		return (
			<div className="min-h-screen min-h-[100dvh] flex items-center justify-center bg-[#eae5e5]">
				<p className="text-brand-text font-sans">Loading...</p>
			</div>
		);
	}

	if (isAuthenticated && !passwordChangeDue) {
		return <AppLayout><HowToPage /></AppLayout>;
	}

	return <div className="min-h-screen min-h-[100dvh] bg-brand-surface"><HowToPage /></div>;
};

/**
 * OfflineShell - Inner wrapper that lives inside OfflineProvider so it can
 * use hooks that depend on OfflineContext (useBeforeUnload).
 *
 * Renders the SyncStatusBar at the very top of the viewport, followed by
 * the matched page. The beforeunload hook warns users about unsaved
 * offline data when they try to close the tab or navigate away.
 */
function OfflineShell() {
	// Scroll to top on every route change so users always start at the top of a new page
	useScrollToTop();
	// Warn the user before leaving the page if there are unsynced offline items
	useBeforeUnload();

	return (
		<>
			{/* Persistent sync/offline status banner — auto-hides when not needed */}
			<SyncStatusBar />
			{/* The matched page — see appRoutes below */}
			<Outlet />
		</>
	);
}

/**
 * RootLayout - The provider stack, rendered once as the root route's element
 * so every page mounts inside it. It lives inside the router rather than
 * around it because AuthProvider and the shell read the location; the
 * matched page renders at OfflineShell's <Outlet />.
 */
function RootLayout() {
	return (
		<AuthProvider>
			<AppProvider>
				<OfflineProvider>
					{/* One Quick Add User modal for the whole app — any
					    people picker opens it via useQuickAddUsers(). */}
					<QuickAddUsersProvider>
						<OfflineShell />
					</QuickAddUsersProvider>
				</OfflineProvider>
			</AppProvider>
		</AuthProvider>
	);
}

/**
 * appRoutes - The route table for the application.
 *
 * Every route is a child of one pathless layout route whose element is the
 * provider stack (RootLayout): the data router renders that once and swaps
 * the matched page into OfflineShell's <Outlet />.
 *
 * Public routes:
 *	 - /login  - Login page (no auth required)
 *	 - *	   - 404 Not Found page (catch-all)
 *
 * Protected routes (require authentication):
 *	 - /					  - Dashboard (home page)
 *	 - /work-orders			  - Work order list
 *	 - /work-orders/create	  - Work order creation form
 *	 - /work-orders/:id		  - Work order detail view
 *	 - /inspections			  - Inspection list
 *	 - /inspections/create	  - Inspection creation form
 *	 - /inspections/:id		  - Inspection detail view
 *	 - /inspections/:id/edit  - Inspection detail view, edit modal auto-opened
 *	 - /buildings			  - Building/location management
 *	 - /profile				  - User profile page (all authenticated users)
 *	 - /admin				  - Admin dashboard (admin and developer roles)
 *	 - /api-keys			  - API key management (developer role only)
 *
 * IMPORTANT: /create routes must be placed BEFORE /:id routes so that
 * "create" is not interpreted as a dynamic :id parameter.
 */
const appRoutes = createRoutesFromElements(
	<Route element={<RootLayout />}>
		{/* --- Public routes --- */}
		<Route path="/login" element={<LoginPage />} />
		<Route path="/auth/callback/o365" element={<OAuthCallbackPage />} />
		<Route path="/auth/callback/google" element={<OAuthCallbackPage />} />

		{/* --- Force password change route --- */}
		{/* Sits outside ProtectedRoute to avoid redirect loop when mustChangePassword is true.
			The page itself checks for an authenticated user. */}
		<Route path="/change-password" element={<ForcePasswordChangePage />} />

		{/* Hidden first-time-user guide — not in the nav; shared by link.
		    Readable signed in or out (see HowToRoute). */}
		<Route path="/how-to" element={<HowToRoute />} />

		{/* --- Protected routes (require authentication) --- */}
		{/* All authenticated pages are wrapped in AppLayout for consistent nav + footer */}
		<Route path="/" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />

		{/* Work Order routes — /create BEFORE /:id to avoid conflict */}
		<Route path="/work-orders" element={<ProtectedRoute><AppLayout><WorkOrdersPage /></AppLayout></ProtectedRoute>} />
		<Route path="/work-orders/create" element={<ProtectedRoute><AppLayout><WorkOrderCreatePage /></AppLayout></ProtectedRoute>} />
		<Route path="/work-orders/:id" element={<ProtectedRoute><AppLayout><WorkOrderDetailPage /></AppLayout></ProtectedRoute>} />
		<Route path="/work-orders/:id/edit" element={<ProtectedRoute><AppLayout><WorkOrderDetailPage /></AppLayout></ProtectedRoute>} />

		{/* Project Work Order routes — /create BEFORE /:id to avoid conflict.
			Page-level role gating happens inside the components and via the
			sidebar / nav (only super_admin and admin see the nav entry). */}
		<Route path="/project-work-orders" element={<ProtectedRoute><AppLayout><ProjectWorkOrdersPage /></AppLayout></ProtectedRoute>} />
		<Route path="/project-work-orders/create" element={<ProtectedRoute><AppLayout><ProjectWorkOrderCreatePage /></AppLayout></ProtectedRoute>} />
		<Route path="/project-work-orders/:id" element={<ProtectedRoute><AppLayout><ProjectWorkOrderDetailPage /></AppLayout></ProtectedRoute>} />
		{/* Edit mode reuses the create page component, which switches
		    on the presence of :id in the URL. */}
		<Route path="/project-work-orders/:id/edit" element={<ProtectedRoute><AppLayout><ProjectWorkOrderCreatePage /></AppLayout></ProtectedRoute>} />

		{/* Inspection routes — /create BEFORE /:id to avoid conflict */}
		<Route path="/inspections" element={<ProtectedRoute><AppLayout><InspectionsPage /></AppLayout></ProtectedRoute>} />
		<Route path="/inspections/create" element={<ProtectedRoute><AppLayout><InspectionCreatePage /></AppLayout></ProtectedRoute>} />
		<Route path="/inspections/:id" element={<ProtectedRoute><AppLayout><InspectionDetailPage /></AppLayout></ProtectedRoute>} />
		<Route path="/inspections/:id/edit" element={<ProtectedRoute><AppLayout><InspectionDetailPage /></AppLayout></ProtectedRoute>} />

		{/* Recurring WO + Inspection schedules — list + detail. The two
		    detail routes share a single component (RecurringSeriesDetailPage)
		    that switches on the URL prefix to resolve the back-link and
		    the per-occurrence date column. */}
		<Route path="/recurring-work-orders" element={<ProtectedRoute><AppLayout><RecurringWorkOrdersPage /></AppLayout></ProtectedRoute>} />
		<Route path="/recurring-work-orders/:id" element={<ProtectedRoute><AppLayout><RecurringSeriesDetailPage /></AppLayout></ProtectedRoute>} />
		<Route path="/recurring-inspections" element={<ProtectedRoute><AppLayout><RecurringInspectionsPage /></AppLayout></ProtectedRoute>} />
		<Route path="/recurring-inspections/:id" element={<ProtectedRoute><AppLayout><RecurringSeriesDetailPage /></AppLayout></ProtectedRoute>} />

		{/* Building management */}
		<Route path="/buildings" element={<ProtectedRoute><AppLayout><BuildingsPage /></AppLayout></ProtectedRoute>} />

		{/* User profile — accessible by all authenticated users */}
		<Route path="/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />

		{/* Admin dashboard — intended for admin and developer roles */}
		<Route path="/admin" element={<ProtectedRoute><AppLayout><AdminPage /></AppLayout></ProtectedRoute>} />

		{/* CMS — Super Admin content management for checklist templates and photo types.
			Page-level role check inside CMSPage gates non-super-admin users with a 403 view. */}
		<Route path="/cms" element={<ProtectedRoute><AppLayout><CMSPage /></AppLayout></ProtectedRoute>} />

		{/* Calendar + Map — visible to every authenticated user; the data feed itself
			is server-side-scoped by role so users only see events they're allowed to see. */}
		<Route path="/calendar" element={<ProtectedRoute><AppLayout><CalendarPage /></AppLayout></ProtectedRoute>} />
		<Route path="/map" element={<ProtectedRoute><AppLayout><MapPage /></AppLayout></ProtectedRoute>} />

		{/* API key management — intended for developer role only */}
		<Route path="/api-keys" element={<ProtectedRoute><AppLayout><ApiKeysPage /></AppLayout></ProtectedRoute>} />

		{/* --- Catch-all 404 route --- */}
		<Route path="*" element={<NotFoundPage />} />
	</Route>,
);

/**
 * The data router. createBrowserRouter rather than the plain component
 * router because only the data router can hold an in-app navigation — the
 * unsaved-changes prompt on the work order and inspection forms runs on its
 * blocker — and it is the setup React Router recommends regardless.
 */
const router = createBrowserRouter(appRoutes);

/**
 * App - The root component that assembles providers and renders routes.
 *
 * The provider nesting order is:
 *	 PersistQueryClientProvider > RouterProvider > RootLayout
 *	   (AuthProvider > AppProvider > OfflineProvider > OfflineShell)
 *
 * PersistQueryClientProvider is outermost so the TanStack Query cache (server
 * state) is available to every provider and page, and is rehydrated from
 * IndexedDB on startup for instant/offline reads. OfflineShell is a thin wrapper
 * that enables hooks dependent on OfflineContext (useBeforeUnload) and renders
 * the SyncStatusBar above the route content.
 */
function App() {
	return (
		<PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
			<RouterProvider router={router} />
		</PersistQueryClientProvider>
	);
}

export default App;
